/**
 * Session Achievement Badge Component
 * Displays a session achievement using BE AchievementType directly
 */

import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { SessionAchievementDisplay, AchievementLevel } from '../types/achievementTypes'
import { AchievementIcon } from './AchievementIcon'
import { INTERAX_COLORS } from '../../../../constants/colors'

export interface SessionAchievementBadgeProps {
  achievement: SessionAchievementDisplay
  className?: string
  size?: 'small' | 'medium' | 'large'
}

/**
 * Get level text from tier
 */
function getTierText(tier: SessionAchievementDisplay['tier']): string {
  switch (tier) {
    case AchievementLevel.GOLD:
      return 'Gold'
    case AchievementLevel.SILVER:
      return 'Silver'
    case AchievementLevel.BRONZE:
      return 'Bronze'
    default:
      return ''
  }
}

/**
 * SessionAchievementBadge - Displays session achievement with icon, title, and tier
 */
export const SessionAchievementBadge: React.FC<SessionAchievementBadgeProps> = ({
  achievement,
  className = '',
  size = 'medium'
}) => {
  const iconSize = React.useMemo(() => {
    switch (size) {
      case 'small':
        return 80
      case 'large':
        return 142
      default:
        return 110
    }
  }, [size])

  const levelText = getTierText(achievement.tier)
  const isCompleted = achievement.tier !== undefined && achievement.tier !== null

  const badgeContent = (
    <Box
      component="article"
      aria-label={`Achievement: ${achievement.title}`}
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: size === 'small' ? 2 : 3,
        gap: 2,
        minWidth: size === 'small' ? 130 : size === 'large' ? 200 : 180,
        maxWidth: size === 'small' ? 130 : size === 'large' ? 200 : 180,
        transition: 'all 0.3s ease-in-out',
        background: 'linear-gradient(144.63deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)',
        border: `1px solid ${INTERAX_COLORS.white08}`,
        boxShadow: `0px 4px 16px rgba(0, 0, 0, 0.2), inset 0px 1px 0px 1px rgba(255, 255, 255, 0.1)`,
        backdropFilter: 'blur(6px)',
        borderRadius: '16px',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0px 8px 24px rgba(255, 171, 64, ${isCompleted ? '0.3' : '0.1'})`
        }
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize,
          height: iconSize
        }}
      >
        <AchievementIcon level={achievement.tier} achievementId={achievement.id} size={iconSize} />
      </Box>

      {/* Text Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          textAlign: 'center'
        }}
      >
        {/* Achievement Title */}
        <Box
          component="h3"
          sx={{
            fontFamily: 'Lexend Deca, sans-serif',
            fontWeight: 700,
            fontSize: size === 'small' ? '14px' : size === 'large' ? '18px' : '16px',
            lineHeight: size === 'small' ? '18px' : size === 'large' ? '22px' : '20px',
            margin: 0,
            padding: '0 4px',
            minHeight: size === 'small' ? '36px' : '40px',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%',
            whiteSpace: 'normal',
            background: isCompleted
              ? `linear-gradient(142.35deg, ${INTERAX_COLORS.sun} 22.68%, #FFD700 42.5%, #FFEEAA 55.31%, #FFD14E 69.6%, ${INTERAX_COLORS.sun} 83.62%)`
              : `linear-gradient(128.64deg, #7A96AC 14.45%, #EAEFF3 27.29%, #C2D4E1 36.93%, #FFFFFF 49.55%, #D4DEE5 58.33%, #ABBDC8 70.46%, #BCCAD7 82.6%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textFillColor: 'transparent'
          }}
        >
          {achievement.title}
        </Box>

        {/* Level Text */}
        <Box
          component="p"
          sx={{
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 500,
            fontSize: '10.87px',
            lineHeight: '14px',
            color: INTERAX_COLORS.typographyFont,
            margin: 0,
            minHeight: '14px',
            opacity: 0.7
          }}
        >
          {levelText}
        </Box>
      </Box>
    </Box>
  )

  // Wrap in tooltip if tooltip text is provided
  if (achievement.tooltip) {
    return (
      <Tooltip title={achievement.tooltip} arrow>
        {badgeContent}
      </Tooltip>
    )
  }

  return badgeContent
}
